import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Mail, Truck, Home, X, Copy, Send, Ban } from "lucide-react";
import { adminApi, type OrderRow } from "@/lib/api";

const STATUS_LABEL: Record<OrderRow["status"], string> = {
  draft: "発注前（下書き）",
  sent: "発注済み",
  shipped: "出荷済み",
  received: "入荷・受領",
  cancelled: "キャンセル",
};
const STATUS_STYLE: Record<OrderRow["status"], string> = {
  draft: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200",
  sent: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
  shipped: "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200",
  received: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
  cancelled: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200",
};
const NEXT: Partial<Record<OrderRow["status"], { to: OrderRow["status"]; label: string }>> = {
  sent: { to: "shipped", label: "出荷済みにする" },
  shipped: { to: "received", label: "入荷・受領にする" },
};
function caseNo(n?: number | null) { return n == null ? "No.----" : "No." + String(n).padStart(4, "0"); }

/** 送信前確認モーダルの状態：生成した件名・本文を運営が編集して送信する。 */
type MailDraft = { orderId: string; subject: string; body: string; to?: string | null; maker?: string | null };

export function OrdersPage() {
  const qc = useQueryClient();
  const { data: orders, isLoading, error } = useQuery({ queryKey: ["orders"], queryFn: () => adminApi.listOrders() });
  const [draft, setDraft] = useState<MailDraft | null>(null);

  const shipMut = useMutation({
    mutationFn: ({ id, ship }: { id: string; ship: "vendor" | "customer" }) => adminApi.setOrderShipTo(id, ship),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["orders"] }),
  });
  const statusMut = useMutation({
    mutationFn: ({ id, st }: { id: string; st: OrderRow["status"] }) => adminApi.setOrderStatus(id, st),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["orders"] }),
  });
  // 送信前確認：send=false でドラフト生成 → モーダルで編集 → 「送信」で send=true。
  const previewMut = useMutation({
    mutationFn: (id: string) => adminApi.generateOrderEmail(id, { send: false }),
    onSuccess: (r, id) => setDraft({ orderId: id, subject: r.subject, body: r.body, to: r.to, maker: r.maker }),
  });

  return (
    <div className="px-4 py-4 space-y-3">
      <h2 className="text-lg font-semibold">メーカー直発注</h2>
      <p className="text-xs text-ink-soft dark:text-white/60">
        落札時に自動生成。配送先（業者宛/顧客宅）を選び、「知らす名義」の発注メールを<strong>送信前に確認・編集</strong>してから送付します。
      </p>
      {isLoading && <p className="text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> 読み込み中...</p>}
      {error && <p className="text-sm text-red-600">読み込みに失敗しました</p>}
      {orders && orders.length === 0 && <p className="text-sm text-ink-soft">発注はまだありません（落札時に自動作成されます）。</p>}

      <ul className="space-y-2">
        {orders?.map((o) => (
          <li key={o.id} className="rounded-xl border border-[var(--color-border)] p-3 sm:p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-black tabular-nums px-2 py-0.5 rounded-md bg-brand-500 text-white">{caseNo(o.case_no)}</span>
                  <span className="font-medium truncate">{o.maker || ""} {o.model || "型番確認中"}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_STYLE[o.status]}`}>{STATUS_LABEL[o.status]}</span>
                </div>
                <p className="text-xs text-ink-soft dark:text-white/60 mt-1">
                  配送先：{o.ship_to === "customer" ? "お客様宅" : "施工業者"}　{o.recipient_name || ""}　{o.recipient_address || ""}
                </p>
              </div>
            </div>
            {o.status !== "cancelled" && (
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <div className="inline-flex rounded-lg border border-[var(--color-border)] overflow-hidden text-xs">
                  <button onClick={() => shipMut.mutate({ id: o.id, ship: "vendor" })} disabled={shipMut.isPending}
                    className={`flex items-center gap-1 px-2.5 py-1.5 ${o.ship_to === "vendor" ? "bg-brand-500 text-white" : ""}`}>
                    <Truck size={13} /> 業者宛
                  </button>
                  <button onClick={() => shipMut.mutate({ id: o.id, ship: "customer" })} disabled={shipMut.isPending}
                    className={`flex items-center gap-1 px-2.5 py-1.5 ${o.ship_to === "customer" ? "bg-brand-500 text-white" : ""}`}>
                    <Home size={13} /> 顧客宅
                  </button>
                </div>
                <button onClick={() => previewMut.mutate(o.id)} disabled={previewMut.isPending}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[var(--color-border)] text-sm">
                  <Mail size={14} /> 発注メールを確認
                </button>
                {NEXT[o.status] && (
                  <button onClick={() => statusMut.mutate({ id: o.id, st: NEXT[o.status]!.to })} disabled={statusMut.isPending}
                    className="px-3 py-1.5 rounded-lg border border-[var(--color-border)] text-sm">{NEXT[o.status]!.label}</button>
                )}
                {(o.status === "draft" || o.status === "sent") && (
                  <button
                    onClick={() => { if (confirm(`${caseNo(o.case_no)} の発注をキャンセルしますか？`)) statusMut.mutate({ id: o.id, st: "cancelled" }); }}
                    disabled={statusMut.isPending}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-red-300 text-red-600 dark:text-red-300 text-sm">
                    <Ban size={14} /> 発注キャンセル
                  </button>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>

      {draft && (
        <MailPreview
          draft={draft}
          onChange={setDraft}
          onClose={() => setDraft(null)}
          onSent={() => { setDraft(null); qc.invalidateQueries({ queryKey: ["orders"] }); }}
        />
      )}
    </div>
  );
}

function MailPreview({
  draft, onChange, onClose, onSent,
}: { draft: MailDraft; onChange: (d: MailDraft) => void; onClose: () => void; onSent: () => void }) {
  const sendMut = useMutation({
    mutationFn: () => adminApi.generateOrderEmail(draft.orderId, { send: true, subject: draft.subject, body: draft.body, to: draft.to ?? undefined }),
  });
  const done = sendMut.isSuccess;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-6">
      <div className="bg-[var(--color-bg)] w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl p-5 space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-bold">発注メール 送信前確認（知らす名義）</h3>
          <button onClick={onClose} className="p-1 text-ink-soft"><X size={18} /></button>
        </div>
        <p className="text-xs text-ink-soft dark:text-white/60">
          内容を確認・編集してから送信してください。「送信」で発注ステータスが「発注済み」になります。
        </p>
        {/* 宛先メーカー（makers.contact から自動解決） */}
        <div className="text-xs rounded-md border border-[var(--color-border)] bg-gray-50/60 dark:bg-white/5 px-3 py-2">
          <span className="text-ink-soft">宛先メーカー：</span>
          {draft.to ? (
            <span className="font-semibold">{draft.maker ? `${draft.maker}（${draft.to}）` : draft.to}</span>
          ) : (
            <span className="text-amber-700 dark:text-amber-300">メール未登録（メーカー管理で連絡先を設定すると自動送信できます）</span>
          )}
        </div>
        {done && (
          <div className={`text-xs rounded-md px-3 py-2 ${sendMut.data?.sent ? "bg-brand-50 text-brand-700 dark:bg-brand-800/30 dark:text-brand-200" : "bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200"}`}>
            {sendMut.data?.sent ? "メーカーへ送信しました。" : (sendMut.data?.note ?? "発注を記録しました（自動送信は無効）。")}
          </div>
        )}
        <label className="block space-y-1">
          <span className="text-xs text-ink-soft">件名</span>
          <input
            value={draft.subject}
            onChange={(e) => onChange({ ...draft, subject: e.target.value })}
            className="w-full text-sm font-semibold border border-[var(--color-border)] rounded-md p-2 bg-transparent"
          />
        </label>
        <label className="block space-y-1">
          <span className="text-xs text-ink-soft">本文</span>
          <textarea
            value={draft.body}
            onChange={(e) => onChange({ ...draft, body: e.target.value })}
            rows={14}
            className="w-full text-sm border border-[var(--color-border)] rounded-md p-2 font-mono bg-transparent"
          />
        </label>
        {sendMut.error && <p className="text-xs text-red-600">{(sendMut.error as Error).message}</p>}
        <div className="flex gap-2">
          <button onClick={() => navigator.clipboard?.writeText(`${draft.subject}\n\n${draft.body}`)}
            className="flex items-center justify-center gap-1 px-3 py-2 rounded-lg border border-[var(--color-border)] text-sm">
            <Copy size={14} /> コピー
          </button>
          {done ? (
            <button onClick={onSent} className="flex-1 py-2 rounded-lg bg-brand-500 text-white text-sm font-semibold">完了して閉じる</button>
          ) : (
            <>
              <button onClick={onClose} className="flex-1 py-2 rounded-lg border border-[var(--color-border)] text-sm">閉じる</button>
              <button
                onClick={() => sendMut.mutate()}
                disabled={sendMut.isPending || !draft.subject.trim() || !draft.body.trim()}
                className="flex-1 flex items-center justify-center gap-1 py-2 rounded-lg bg-brand-500 text-white text-sm font-semibold disabled:opacity-50">
                <Send size={14} /> {sendMut.isPending ? "送信中..." : "送信（発注済みに）"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
